import React, { useEffect, useState } from 'react';
import { Table, Button, Spin, Input, Row, Col, Modal } from 'antd';
import apiRequest from '../hooks/ApiRequest';
import Layouts from '../components/Layouts';

const ReinsurerAmountDueReport = () => {
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [loggedInInsurerId, setLoggedInInsurerId] = useState(null);

  // Modal state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPolicies, setSelectedPolicies] = useState([]);

  // Load logged-in user info on mount
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const userObj = JSON.parse(userStr);
        setLoggedInInsurerId(userObj.UserId);
      } catch (err) {
        console.error('Failed to parse logged in user:', err);
      }
    }
  }, []);

  // Fetch report once we have loggedInInsurerId
  useEffect(() => {
    if (loggedInInsurerId) {
      fetchReport();
    }
  }, [loggedInInsurerId]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const apiUrl = `${process.env.REACT_APP_BASE_URL}/Policy/GetReinsurerAmountDueReport/ReinsuranceReportAmountDue/${loggedInInsurerId}`;
      const res = await apiRequest(apiUrl, 'GET');

      if (res.success && Array.isArray(res.data)) {
        const transformedData = res.data.map((item) => ({
          ...item,
          insurer: item.reinsurerName || '-', // for display
          reinsurerId: item.reinsurerId || null,
          policies: item.policyNumbers || [],
          totalPolicies: item.totalPolicies || 0,
          reinsuredAmount: item.reinsuredAmount || 0,
        }));

        setReportData(transformedData);
        setFilteredData(transformedData);
      } else {
        setReportData([]);
        setFilteredData([]);
      }
    } catch (error) {
      console.error('Failed to fetch report:', error);
      setReportData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  // Search filter on insurer name
  const handleSearch = (value) => {
    setSearchText(value);
    const search = value.toLowerCase();
    const filtered = reportData.filter((item) =>
      item.insurer.toLowerCase().includes(search)
    );
    setFilteredData(filtered);
  };

  const clearFilters = () => {
    setSearchText('');
    setFilteredData(reportData);
  };

  // Format amount in INR currency
  const formatCurrency = (value) =>
    value != null
      ? Number(value).toLocaleString('en-IN', {
          style: 'currency',
          currency: 'INR',
        })
      : '-';

  // Open modal with policies for a selected reinsurer
  const openPoliciesModal = async (reinsurerId) => {
    setLoading(true);
    try {
      const apiUrl = `${process.env.REACT_APP_BASE_URL}/Policy/GetPoliciesForReinsurer/ReinsuranceReportPolicies/${reinsurerId}`;
      const res = await apiRequest(apiUrl, 'GET');

      if (Array.isArray(res)) {
        setSelectedPolicies(res);
      } else if (res.success && Array.isArray(res.data)) {
        setSelectedPolicies(res.data);
      } else {
        setSelectedPolicies([]);
      }
      setIsModalVisible(true);
    } catch (error) {
      console.error('Failed to fetch policies:', error);
      setSelectedPolicies([]);
      setIsModalVisible(true);
    } finally {
      setLoading(false);
    }
  };

  // Columns for the main reinsurer report table
  const columns = [
    { key: 'insurer', title: 'Insurer', dataIndex: 'insurer' },
    {
      key: 'reinsuredAmount',
      title: 'Reinsured Amount',
      dataIndex: 'reinsuredAmount',
      render: (amount) => formatCurrency(amount),
      sorter: (a, b) => a.reinsuredAmount - b.reinsuredAmount,
    },
    { key: 'totalPolicies', title: 'Total Policies', dataIndex: 'totalPolicies', sorter: (a, b) => a.totalPolicies - b.totalPolicies },
    {
      key: 'viewPolicies',
      title: 'Actions',
      render: (_, record) => (
        <Button
          type="primary"
          style={{ backgroundColor: '#0d80ebff', borderColor: '#1890ff' }}
          onClick={() => openPoliciesModal(record.reinsurerId)}
        >
          View Policies
        </Button>
      ),
    },
  ];

  // Columns for modal showing policies of a reinsurer
  const modalColumns = [
    {
      key: 'sequenceNumber',
      title: 'Sequence Number',
      render: (_, __, index) => index + 1,
    },
    { key: 'policyNumber', title: 'Policy Number', dataIndex: 'policyNumber' },
    {
      key: 'actions',
      title: 'Actions',
      render: (record) => (
        <Button
          type="link"
          onClick={() => {
            window.location.href = '/policies'
          }}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <Layouts title="Reinsurer Amount Due Report" classname="dashboard">
      <Spin className="loader-icon-fixed" spinning={loading} size="large" />

      <div className="dash-bg-white">
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={24} md={24} lg={12}>
            <div className="section-top-heading">
              <h3 style={{ color: '#0C1362', fontWeight: 600, fontSize: '20px' }}>
                Reinsurer Amount Due Report
              </h3>
            </div>
          </Col>
        </Row>
      </div>

      {/* Filters */}
      <div className="dash-bg-white">
        <div className="m-b-20">
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} md={18} lg={20} className="custom-statcards">
              <Input
                placeholder="Search by Insurer"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: 300 }}
                allowClear
              />
              &nbsp;
              <Button onClick={clearFilters}>Clear</Button>
            </Col>
          </Row>
        </div>
      </div>

      {/* Table */}
      <div className="table-grid-bx">
        <Table
          className="table-scroll-auto"
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="reinsurerId" 
          pagination={{ pageSize: 10 }}
        />
      </div>

      {/* Modal */}
      <Modal
        title="Policies"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={700}
      >
        <Table
          columns={modalColumns}
          dataSource={selectedPolicies}
          rowKey="policyNumber"
          pagination={false}
          scroll={{ y: 300 }}
        />
      </Modal>
    </Layouts>
  );
};

export default ReinsurerAmountDueReport;
